import { useState, useEffect, useCallback } from 'react';
import { api, Organization } from '../api';
import { StatusBadge } from './ui';
import { CreateOrgModal } from './modals';

interface OrganizationListProps {
  onSelectOrg: (id: string) => void;
}

export function OrganizationList({ onSelectOrg }: OrganizationListProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [deletedOrganizations, setDeletedOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      // Load active organizations (excluding deleted)
      const result = await api.listOrganizations({ search, status: statusFilter || undefined });
      // Filter out deleted from main list (in case backend returns them)
      setOrganizations(result.organizations.filter(org => org.status !== 'deleted'));
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const loadDeletedOrganizations = useCallback(async () => {
    try {
      const result = await api.listOrganizations({ status: 'deleted' });
      setDeletedOrganizations(result.organizations);
    } catch (err) {
      console.error('Failed to load deleted organizations:', err);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  // Load deleted count on initial mount
  useEffect(() => {
    loadDeletedOrganizations();
  }, [loadDeletedOrganizations]);

  const handleCreate = async (name: string, slug: string) => {
    await api.createOrganization({ name, slug });
    setShowCreateModal(false);
    loadOrganizations();
  };

  return (
    <div className="page">
      <header className="page-header">
        <h1>Organizations</h1>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + New Organization
        </button>
      </header>

      <div className="filters">
        <input
          type="text"
          placeholder="Search organizations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td><code>{org.slug}</code></td>
                <td><StatusBadge status={org.status} /></td>
                <td>{new Date(org.created_at).toLocaleDateString()}</td>
                <td>
                  <button className="btn-link" onClick={() => onSelectOrg(org.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={5} className="empty-state">No organizations found</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Deleted Organizations Section */}
      <div className="deleted-section">
        <button
          className="deleted-section-toggle"
          onClick={() => setShowDeleted(!showDeleted)}
        >
          <span className="toggle-icon">{showDeleted ? '▼' : '▶'}</span>
          Deleted Organizations
          {deletedOrganizations.length > 0 && (
            <span className="deleted-count">({deletedOrganizations.length})</span>
          )}
        </button>

        {showDeleted && (
          <div className="deleted-section-content">
            {deletedOrganizations.length === 0 ? (
              <p className="empty-state">No deleted organizations</p>
            ) : (
              <table className="data-table deleted-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Deleted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedOrganizations.map((org) => (
                    <tr key={org.id}>
                      <td>{org.name}</td>
                      <td><code>{org.slug}</code></td>
                      <td>{new Date(org.updated_at).toLocaleDateString()}</td>
                      <td>
                        <button className="btn-link" onClick={() => onSelectOrg(org.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateOrgModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
